import React, { useEffect, useState } from "react";
import axios from "axios";

const fmtMoney = (n) => (n || n === 0 ? `£${Number(n).toFixed(2)}` : "—");
const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";
const EXTRA_PRICES = {
  travelpak_bundle: 24.99,
  sim_o2: 11.99,
  adapter: 13.99,
  meet_greet: 10.99,
};

const getBrandContext = () => {
  const siteMode = process.env.REACT_APP_SITE_MODE || "ccg";
  if (siteMode === "minibusgrab") {
    return {
      brandName: "MinibusGrab",
      platformName: "CheapCabGrab",
      successMessage:
        "Thanks for booking your MinibusGrab journey. Your payment has been processed securely by CheapCabGrab.",
      safeTravelIcon: "🚌",
      background:
        "linear-gradient(135deg, rgba(7,17,31,0.96), rgba(21,62,91,0.9)), url('/ajp-bg.png') center/cover no-repeat",
    };
  }
  if (siteMode === "cheapcoachgrab") {
    return {
      brandName: "CheapCoachGrab",
      platformName: "CheapCabGrab",
      successMessage:
        "Thanks for booking your CheapCoachGrab journey. Your payment has been processed securely by CheapCabGrab.",
      safeTravelIcon: "🚌",
      background:
        "linear-gradient(135deg, rgba(7,17,31,0.96), rgba(21,62,91,0.9)), url('/ajp-bg.png') center/cover no-repeat",
    };
  }
  return {
    brandName: "CheapCabGrab",
    platformName: "CheapCabGrab",
    successMessage: "Thanks for booking with CheapCabGrab. Your payment has been confirmed.",
    safeTravelIcon: "🚖",
    background: "url('/bg-taxi.png')",
  };
};

function buildExtrasItems(rawExtras, fallbackRaw) {
  const source = rawExtras ?? fallbackRaw ?? {};
  if (Array.isArray(source) && source.length) return source;
  let extras = source || {};
  if (typeof extras === "string") {
    try {
      extras = JSON.parse(extras);
    } catch (_) {
      extras = {};
    }
  }

  const items = [];
  if (toBool(extras.travelpak_bundle)) {
    items.push({ label: "Travelpak bundle (SIM + adapter)", amount: EXTRA_PRICES.travelpak_bundle });
  } else {
    if (toBool(extras.sim_o2)) items.push({ label: "UK SIM (O2)", amount: EXTRA_PRICES.sim_o2 });
    if (toBool(extras.adapter)) items.push({ label: "Travel adapter", amount: EXTRA_PRICES.adapter });
  }
  if (toBool(extras.meet_greet)) items.push({ label: "Meet & greet", amount: EXTRA_PRICES.meet_greet });
  return items;
}

const PaymentSuccess = () => {
  const brandContext = getBrandContext();
  const [state, setState] = useState({
    loading: true,
    ok: false,
    data: null,
    message: ""
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const session_id = params.get("session_id");
    const ref = params.get("ref");

    const API_BASE = process.env.REACT_APP_API_URL || "https://api.cheapcabgrab.com";

    async function verifyTokenFlow(token) {
      try {
        const res = await axios.get(`${API_BASE}/api/payments/verify`, {
          params: { token },
          withCredentials: true,
        });

        if (res.data?.ok && res.data?.booking) {
          setState({ loading: false, ok: true, data: res.data.booking });
        } else {


setState({
  loading: false,
  ok: false,
  message:
    res.data?.error === "Invalid or expired link"
      ? "⚠️ This confirmation link has expired or is no longer valid. " +
        "For security, CheapCabGrab payment confirmation links automatically expire after a short time. " +
        "If you need assistance, please contact support@cheapcabgrab.com."
      : res.data?.error ||
        "⚠️ We couldn’t confirm this payment. Please contact support@cheapcabgrab.com.",
});




        }
      } catch (err) {
        setState({
          loading: false,
          ok: false,
          message:
            err.response?.data?.error ||
            "Verification failed. Please contact support.",
        });
      }
    }

    async function confirmSessionFlow() {
      try {
        const res = await axios.get(`${API_BASE}/api/payments/confirm`, {
          params: { session_id, ref },
          withCredentials: true,
        });

        // ✅ If backend now includes token (new flow)
        if (res.data?.ok && res.data?.token) {
          // redirect to token URL for expiry enforcement (stay on the new success page)
          window.location.href = `/payment-success-new?token=${res.data.token}`;
          return;
        }

        // ✅ Legacy fallback (no token field returned)
        if (res.data?.ok && res.data?.booking) {
          setState({ loading: false, ok: true, data: res.data.booking });
        } else {
          setState({
            loading: false,
            ok: false,
            message: res.data?.message || "We couldn't confirm the payment yet.",
          });
        }
      } catch (err) {
        setState({
          loading: false,
          ok: false,
          message:
            err.response?.data?.error ||
            "Confirmation failed. Please contact support.",
        });
      }
    }

    if (token) {
      // ✅ New secure flow
      verifyTokenFlow(token);
    } else if (session_id && ref) {
      // ✅ Legacy flow (used after Stripe redirect)
      confirmSessionFlow();
    } else {
      setState({ loading: false, ok: false, message: "Missing confirmation details." });
    }
  }, []);

  if (state.loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "4rem" }}>
        <p>⏳ Confirming payment…</p>
      </div>
    );
  }

  const b = state.data;
  const breakdown = b?.fare_breakdown || {};
  const extrasItems = buildExtrasItems(
    b?.extras_breakdown || breakdown.items,
    b?.extras_json || breakdown.extras
  );
  const extrasTotalValue = b?.extras_total_gbp ?? breakdown.extras;
  if (extrasItems.length === 0 && Number(extrasTotalValue) > 0) {
    extrasItems.push({ label: "Extras", amount: Number(extrasTotalValue) });
  }
  const extrasTotalFromItems = extrasItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const baseFare = b?.base_fare_gbp ?? breakdown.base ?? b?.baseFare ?? b?.fare ?? 0;
  const extrasTotal = b?.extras_total_gbp ?? breakdown.extras ?? extrasTotalFromItems;
  const discount = b?.discount_gbp ?? breakdown.discount ?? 0;
  const finalFare = b?.fare ?? breakdown.total ?? 0;
  const hasExtras = extrasItems.length > 0;
  const hasDiscount = Number(discount) > 0;
  const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center" };
  console.log("🧾 PaymentSuccessnew data", {
    ref: b?.ref_code,
    baseFare,
    extrasTotal,
    discount,
    finalFare,
    extrasItems,
    raw: b,
  });

  return (
    <div
      style={{
        background: brandContext.background,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <div
        style={{
          background: "rgba(255,255,255,0.92)",
          borderRadius: "12px",
          padding: "2rem 3rem",
          boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
          maxWidth: "600px",
          textAlign: "center"
        }}
      >
        {state.ok ? (
          <>
            <h2 style={{ color: "green", marginBottom: "1rem" }}>✅ Payment Received</h2>
            <p style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>
              {brandContext.successMessage}
            </p>

            <div
              style={{
                textAlign: "left",
                fontSize: "0.95rem",
                margin: "1rem 0",
                lineHeight: "1.6"
              }}
            >
              <p><strong>Reference:</strong> {b.ref_code}</p>
              <p><strong>Pickup:</strong> {b.pickup_location}</p>
              <p><strong>Drop-off:</strong> {b.dropoff_location}</p>
              <p><strong>Date/Time:</strong> {new Date(b.pickup_datetime).toString()}</p>
              <p><strong>Vehicle:</strong> {b.vehicle_type?.toUpperCase()}</p>
              <div style={{ marginTop: "10px" }}>
                <div><strong>Fare breakdown:</strong></div>
                <div style={rowStyle}>
                  <span>Base fare</span>
                  <span>{fmtMoney(baseFare)}</span>
                </div>
                {hasExtras ? (
                  <>
                    <div style={{ ...rowStyle, color: "#333" }}>
                      <span>Extras</span>
                      <span>{fmtMoney(extrasTotal)}</span>
                    </div>
                    <div style={{ marginLeft: 8, marginTop: 4 }}>
                      {extrasItems.map((item, idx) => (
                        <div key={idx} style={{ ...rowStyle, fontSize: "0.9rem", color: "#555" }}>
                          <span>{item.label}</span>
                          <span>{fmtMoney(item.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={rowStyle}>
                    <span>Extras</span>
                    <span>{fmtMoney(extrasTotal)}</span>
                  </div>
                )}
                {hasDiscount && (
                  <div style={rowStyle}>
                    <span>Discount</span>
                    <span>- {fmtMoney(discount)}</span>
                  </div>
                )}
                <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 6, paddingTop: 6, ...rowStyle }}>
                  <strong>Total paid</strong>
                  <strong>{fmtMoney(finalFare)}</strong>
                </div>
              </div>
            </div>

            <p style={{ marginTop: "1rem" }}>
              We’ll be in touch if we need anything else.<br/>Safe travels! {brandContext.safeTravelIcon}
            </p>

            <button
              onClick={() => (window.location.href = "/")}
              style={{
                background: "#ffc107",
                color: "#000",
                padding: "0.8rem 1.5rem",
                border: "none",
                borderRadius: "6px",
                marginTop: "1.5rem",
                fontWeight: "600",
                cursor: "pointer"
              }}
            >
              ⬅ Back Home
            </button>
          </>
        ) : (
          <>
            <h2 style={{ color: "#d9534f" }}>⚠️ Payment Status</h2>
            <p>{state.message}</p>
            <button
              onClick={() => (window.location.href = "/")}
              style={{
                background: "#007bff",
                color: "#fff",
                padding: "0.8rem 1.5rem",
                border: "none",
                borderRadius: "6px",
                marginTop: "1rem",
                cursor: "pointer"
              }}
            >
              Back Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
